import React, { useEffect, useState } from "react";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import AppShell from "../components/AppShell";
import StatusBadge, { ocrStatusVariant, docStatusVariant } from "../components/StatusBadge";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  FileStack, Inbox, ShieldAlert, FolderTree, Mailbox, Users, ArrowUpRight,
} from "lucide-react";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="ds-card p-5 flex items-start justify-between" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div>
      <div className="ds-overline">{label}</div>
      <div className="font-display text-3xl font-bold tracking-tight mt-1">
        {value}
      </div>
    </div>
    <div
      className="w-9 h-9 flex items-center justify-center border"
      style={{ background: accent || "#F7F7F8", borderColor: "#E5E5E9" }}
    >
      <Icon size={18} />
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    docs: 0,
    ocrQueue: 0,
    deletionPending: 0,
    folders: 0,
    teams: 0,
    users: 0,
  });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [docs, ocrQueue, dels, folders, teams, users] = await Promise.all([
          api.get("/documents").catch(() => ({ data: [] })),
          api.get("/documents/ocr-queue").catch(() => ({ data: [] })),
          api.get("/deletion-requests", { params: { status: "pending" } }).catch(() => ({ data: [] })),
          api.get("/folders").catch(() => ({ data: [] })),
          api.get("/teams").catch(() => ({ data: [] })),
          user.role === "super_admin" ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setStats({
          docs: docs.data.length,
          ocrQueue: ocrQueue.data.length,
          deletionPending: dels.data.length,
          folders: folders.data.length,
          teams: teams.data.length,
          users: users.data.length,
        });
        setRecent(docs.data.slice(0, 8));
      } catch {}
    })();
  }, [user.role]);

  return (
    <AppShell title="Operations Overview">
      <div className="ds-overline mb-2">
        Logged in as {user.name} · {ROLE_LABELS[user.role]}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-px bg-[#E5E5E9] border border-[#E5E5E9]" data-testid="dashboard-stats">
        <Stat label="Documents" value={stats.docs} icon={FileStack} />
        <Stat label="OCR Queue" value={stats.ocrQueue} icon={Inbox} accent="#FFFAEB" />
        <Stat label="Pending Deletion" value={stats.deletionPending} icon={ShieldAlert} accent="#FEF3F2" />
        <Stat label="Folders" value={stats.folders} icon={FolderTree} />
        <Stat label="Teams" value={stats.teams} icon={Mailbox} />
        {user.role === "super_admin" && <Stat label="Users" value={stats.users} icon={Users} />}
      </div>

      <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 ds-card">
          <div className="flex items-center justify-between p-4 border-b border-[#E5E5E9]">
            <div>
              <div className="ds-overline">Recent intake</div>
              <h3 className="font-display text-lg font-semibold tracking-tight">Latest documents</h3>
            </div>
            <Link to="/explorer" className="ds-btn-ghost flex items-center gap-1" data-testid="view-all-docs">
              View all <ArrowUpRight size={13} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Type</th>
                  <th>OCR</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-[#8A8A93] py-8">
                      No documents yet.
                    </td>
                  </tr>
                )}
                {recent.map((d) => (
                  <tr key={d.id} data-testid={`recent-doc-${d.id}`}>
                    <td className="font-mono text-[12px] truncate max-w-[280px]">{d.filename}</td>
                    <td>{d.document_type || <span className="text-[#8A8A93]">—</span>}</td>
                    <td>
                      <StatusBadge variant={ocrStatusVariant(d.ocr_status)}>
                        {d.ocr_status}
                      </StatusBadge>
                    </td>
                    <td>
                      <StatusBadge variant={docStatusVariant(d.status)}>{d.status}</StatusBadge>
                    </td>
                    <td>{d.upload_source}</td>
                    <td className="font-mono text-[11px] text-[#4A4A52]">
                      {new Date(d.upload_date).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ds-card p-5">
          <div className="ds-overline">Quick actions</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mb-4">Jump to</h3>
          <div className="space-y-2">
            <Link className="ds-btn-secondary w-full text-left flex items-center justify-between" to="/upload">
              Upload a document <ArrowUpRight size={13} />
            </Link>
            <Link className="ds-btn-secondary w-full text-left flex items-center justify-between" to="/search">
              Run a search <ArrowUpRight size={13} />
            </Link>
            <Link className="ds-btn-secondary w-full text-left flex items-center justify-between" to="/ocr-queue">
              Manual classification queue <ArrowUpRight size={13} />
            </Link>
            {user.role !== "viewer" && (
              <Link className="ds-btn-secondary w-full text-left flex items-center justify-between" to="/email-intake">
                Simulate email intake <ArrowUpRight size={13} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
